import {
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Plus, Trash2, ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../../components/shared/PageHeader';
import {
  ReturnToAnalysisBanner,
  invalidateSessionGroupQueries,
} from '../../../features/session-groups';
import { sessionGroupStore } from '../../../features/session-groups';
import type { SessionGroupListItem } from '../../../features/session-groups';

const formatDate = (value?: string | null) => {
  if (!value) return 'n/a';
  return new Date(value).toLocaleString('it-IT');
};

export function SessionGroupsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const fromAnalysis = searchParams.get('fromAnalysis');
  const [groups, setGroups] = useState<SessionGroupListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SessionGroupListItem | null>(
    null
  );
  const [deleteOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);

  const loadGroups = () => {
    setIsLoading(true);
    sessionGroupStore
      .listGroups()
      .then(setGroups)
      .catch(error => {
        notifications.show({
          color: 'red',
          title: 'Errore caricamento gruppi',
          message:
            error instanceof Error
              ? error.message
              : 'Impossibile recuperare i gruppi di sessioni.',
        });
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadGroups();
  }, []);

  const handleOpenDelete = (group: SessionGroupListItem) => {
    setDeleteTarget(group);
    openDeleteModal();
  };

  const handleCloseDelete = () => {
    setDeleteTarget(null);
    closeDeleteModal();
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setDeletingId(deleteTarget.groupId);
    try {
      await sessionGroupStore.deleteGroup(deleteTarget.groupId);
      invalidateSessionGroupQueries(queryClient, deleteTarget.groupId);
      notifications.show({
        color: 'teal',
        title: 'Gruppo eliminato',
        message: `Il gruppo "${deleteTarget.name}" è stato eliminato.`,
      });
      handleCloseDelete();
      loadGroups();
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Eliminazione non riuscita',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile eliminare il gruppo.',
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <PageHeader
        title='Gruppi sessioni'
        subtitle='Raggruppa le sessioni completed per riutilizzarle nelle analisi di ricerca.'
        breadcrumbs={[
          { label: 'Sessioni', href: '/sessions' },
          { label: 'Gruppi sessioni' },
        ]}
        backUrl='/sessions'
        actions={
          <Button
            leftSection={<Plus size={16} />}
            onClick={() => navigate('/sessions/group/new')}
          >
            Crea gruppo
          </Button>
        }
      />

      <Divider my='md' />

      {fromAnalysis && <ReturnToAnalysisBanner analysisId={fromAnalysis} />}

      {isLoading ? (
        <Stack align='center' py='xl'>
          <Loader />
          <Text size='sm' c='dimmed'>
            Caricamento gruppi…
          </Text>
        </Stack>
      ) : groups.length === 0 ? (
        <Text c='dimmed'>Nessun gruppo creato. Crea il primo gruppo.</Text>
      ) : (
        <ScrollArea type='auto' offsetScrollbars>
          <Table striped highlightOnHover stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Descrizione</Table.Th>
                <Table.Th>N. sessioni</Table.Th>
                <Table.Th>Aggiornato</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {groups.map(group => (
                <Table.Tr
                  key={group.groupId}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/sessions/group/${group.groupId}`)}
                >
                  <Table.Td fw={600}>{group.name}</Table.Td>
                  <Table.Td>
                    <Text size='sm' c='dimmed' lineClamp={2}>
                      {group.description || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>{group.sessionCount}</Table.Td>
                  <Table.Td>{formatDate(group.updatedAt)}</Table.Td>
                  <Table.Td>
                    <Group gap='xs' justify='flex-end'>
                      <Button
                        variant='light'
                        size='compact-sm'
                        rightSection={<ArrowRight size={14} />}
                        onClick={event => {
                          event.stopPropagation();
                          navigate(`/sessions/group/${group.groupId}`);
                        }}
                      >
                        Apri
                      </Button>
                      <Button
                        variant='light'
                        color='red'
                        size='compact-sm'
                        leftSection={<Trash2 size={14} />}
                        loading={deletingId === group.groupId}
                        onClick={event => {
                          event.stopPropagation();
                          handleOpenDelete(group);
                        }}
                      >
                        Elimina
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}

      <Modal
        opened={deleteOpened}
        onClose={handleCloseDelete}
        title='Elimina gruppo'
        centered
      >
        <Stack gap='md'>
          <Text size='sm'>
            Confermi l&apos;eliminazione del gruppo{' '}
            <strong>{deleteTarget?.name}</strong>? Le analisi che lo
            referenziano non avranno più sessioni finché non ne selezioni un
            altro.
          </Text>
          <Group justify='flex-end'>
            <Button variant='outline' onClick={handleCloseDelete}>
              Annulla
            </Button>
            <Button
              color='red'
              loading={Boolean(deletingId)}
              onClick={handleConfirmDelete}
            >
              Elimina
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
